/**
 * @typedef {Record<string, MetaData>[]} MetaDataVariants - string represents the variant id
 *
 * @typedef {Object} MetaData
 * @property {string} type - The type of media ("image" or "video").
 * @property {string} thumbnail - The URL of the thumbnail image.
 * @property {string} source - The URL of the full-size media source.
 */

export default class ProductImagePicker {
  /**
   * Initialize the ProductImagePicker
   * @param {Object} ProductImageMobileCarousel - Mobile carousel class
   * @param {Object[]} [customImages=null] - Optional custom images
   */
  constructor(ProductImageMobileCarousel, customImages = null) {
    this.customImages = customImages

    // Parse metadata from Shopify custom data
    const shopifyData = window.Shopify?.customData?.productImagePicker || {}

    /** @type {MetaDataVariants} */
    this.metaData = shopifyData.metaData ? JSON.parse(shopifyData.metaData.replace(/'/g, '"')) : []

    /** @type {MetaData[]} */
    this.bundleImages = shopifyData.bundleImages ? JSON.parse(shopifyData.bundleImages.replace(/'/g, '"')) : null

    /** @type {boolean} */
    this.includesBundles = shopifyData.includesBundles || false

    // Get DOM elements
    /** @type {HTMLElement|null} */
    this.imageContainer = document.getElementById("display-image-container")
    /** @type {HTMLElement|null} */
    this.thumbContainer = document.getElementById("thumbnail-container")
    /** @type {HTMLImageElement|null} */
    this.displayImage = document.getElementById("display-image")
    /** @type {HTMLVideoElement|null} */
    this.videoPlayer = document.getElementById("video-element")

    // Initialize mobile carousel
    if (ProductImageMobileCarousel) {
      new ProductImageMobileCarousel()
    }

    this.build()
  }

  /**
   * Initialize the component
   */
  build() {
    if (!this.videoPlayer || !this.imageContainer) return

    this.videoPlayer.style.display = "none"
    this.imageContainer.addEventListener("touchend", this.tapImageAdvance.bind(this))
  }

  /**
   * Handle tap to advance to next image
   * @param {TouchEvent} event - Touch event
   */
  tapImageAdvance(event) {
    if (!event.target || !this.imageContainer) return

    // Don't advance if tapping on a dot
    if (event.target.classList.contains("dot")) return

    const currentDots = this.imageContainer.querySelectorAll(".dot")
    if (!currentDots.length) return

    // Get current source
    let src = null
    if (event.target.id === "video-element") {
      const srcElement = event.target.querySelector("source")
      if (!srcElement?.src) return
      src = srcElement.src.split("//")[1]
    } else if (event.target.src) {
      src = event.target.src.split("//")[1]
    } else {
      return
    }

    // Find current dot and advance to next
    for (let i = 0; i < currentDots.length; i++) {
      if (currentDots[i].dataset.source?.split("//")[1] === src) {
        const nextIndex = i === currentDots.length - 1 ? 0 : i + 1
        const newType = currentDots[nextIndex].dataset.type
        const newSource = currentDots[nextIndex].dataset.source

        if (newType && newSource) {
          this.selectImage(newType, newSource)
        }
        break
      }
    }
  }

  /**
   * Select and display an image or video
   * @param {string} mediaType - Type of media ("image" or "video")
   * @param {string} mediaSource - URL of the media source
   */
  selectImage(mediaType, mediaSource) {
    if (!this.displayImage || !this.videoPlayer || !this.imageContainer) return

    if (mediaType === "video") {
      // Display video
      this.displayImage.style.display = "none"

      const videoSource = document.getElementById("video-source")
      if (videoSource) {
        videoSource.src = mediaSource
      }

      this.videoPlayer.style.display = null
      this.videoPlayer.load()

      // Only play if video is ready
      this.videoPlayer.addEventListener(
        "canplay",
        () => {
          this.videoPlayer.play().catch((err) => {
            console.error("Error playing video:", err)
          })
        },
        { once: true },
      )
    } else {
      // Display image
      this.displayImage.style.display = null
      this.videoPlayer.style.display = "none"
      this.imageContainer.style.height = ""
      this.displayImage.src = mediaSource
      this.displayImage.fetchPriority = "high"
    }

    this.updateThumbs(mediaSource)
  }

  /**
   * Update thumbnail highlighting based on selected media
   * @param {string} source - URL of the selected media
   */
  updateThumbs(source) {
    if (!this.thumbContainer) return

    const thumbs = this.thumbContainer.children

    for (let i = 0; i < thumbs.length; i++) {
      if (thumbs[i].dataset.source === source) {
        thumbs[i].classList.remove("opacity-40")
      } else {
        thumbs[i].classList.add("opacity-40")
      }
    }
  }

  /**
   * Dispatch event to update product image carousel
   * @param {MetaData[]} mediaData - Media data for carousel
   * @param {boolean} [isSuggestedPairing=false] - Whether this is a suggested pairing
   */
  prodImageCarouselHandler(mediaData, isSuggestedPairing = false) {
    const updatePromImageCarousel = new CustomEvent("updateprodimagecarousel", {
      detail: {
        mediaData,
        isSuggestedPairing,
      },
    })
    document.dispatchEvent(updatePromImageCarousel)
  }

  /**
   * Display thumbnails for a selected variant
   * @param {number|string} selectedVariant - ID of the selected variant
   * @param {string} productTitle - Title of the product
   * @param {boolean} [isSuggestedPairing=false] - Whether this is a suggested pairing
   * @param {boolean} [includeBundle=false] - Whether to include bundle images
   * @param {string} [productImage] - Featured product image URL
   */
  displayThumbs(selectedVariant, productTitle, isSuggestedPairing = false, includeBundle = false, productImage) {
    if (!this.thumbContainer) return

    if (this.customImages) {
      this.handleCustomImages(this.customImages, selectedVariant, productTitle)
    } else {
      const imageData = this.metaData.find((element) => element[selectedVariant])

      if (imageData) {
        this.handleMetaDataImages(
          imageData,
          selectedVariant,
          productTitle,
          isSuggestedPairing,
          includeBundle,
          productImage,
        )
      }
    }
  }

  /**
   * Handle custom images for thumbnails
   * @param {Object[]} customImages - Custom images array
   * @param {string} variantTitle - Title of the variant
   * @param {string} productTitle - Title of the product
   */
  handleCustomImages(customImages, variantTitle, productTitle) {
    if (!this.thumbContainer || !this.displayImage) return

    // Find images for this variant or use all custom images
    const images = customImages.find((imageMap) => imageMap?.title === variantTitle)?.images || customImages

    this.thumbContainer.innerHTML = ""
    const carouselMedia = []
    let totalThumbs = 0
    let defaultImage = null

    // Create thumbnails for each image (up to 4)
    for (const [index, image] of images.entries()) {
      if (index === 0) defaultImage = image
      if (totalThumbs === 4) break

      const imageData = {
        source: image,
        thumbnail: image,
        type: "image",
      }

      carouselMedia.push(imageData)
      this.thumbContainer.appendChild(this.createThumbnail(imageData, productTitle, index))

      totalThumbs++
    }

    // If the current image is already displayed, just update thumbnails
    if (this.displayImage.dataset.src === defaultImage) {
      this.updateThumbs(defaultImage.source)
      return
    }

    // Otherwise, select the default image
    if (defaultImage) {
      this.displayImage.dataset.src = defaultImage
      this.selectImage("image", defaultImage)
    }

    this.prodImageCarouselHandler(carouselMedia)
  }

  /**
   * Handle metadata images for thumbnails
   * @param {Object} imageData - Image metadata
   * @param {number|string} selectedVariantId - ID of the selected variant
   * @param {string} productTitle - Title of the product
   * @param {boolean} isSuggestedPairing - Whether this is a suggested pairing
   * @param {boolean} includeBundle - Whether to include bundle images
   * @param {string} [productImage] - Featured product image URL
   */
  handleMetaDataImages(imageData, selectedVariantId, productTitle, isSuggestedPairing, includeBundle, productImage) {
    if (!this.thumbContainer || !this.displayImage || !imageData[selectedVariantId]) return

    this.thumbContainer.innerHTML = ""
    const carouselMedia = []
    let totalThumbs = 0
    let defaultImage = null

    // Handle bundle images if needed
    if (this.includesBundles && includeBundle && isSuggestedPairing && this.bundleImages?.length) {
      for (const [index, bundleImage] of this.bundleImages.entries()) {
        if (totalThumbs === 4) break

        carouselMedia.push(bundleImage)
        this.thumbContainer.appendChild(this.createThumbnail(bundleImage, productTitle, index))

        totalThumbs += 1
      }

      defaultImage = this.bundleImages[0]
    }

    // Set default image
    defaultImage = defaultImage || imageData[selectedVariantId][0]

    // Ensure default image has thumbnail and source
    if (defaultImage && !defaultImage.thumbnail && productImage) {
      defaultImage.thumbnail = productImage
      defaultImage.source = productImage
    }

    // Ensure first variant image has thumbnail and source
    if (imageData[selectedVariantId][0] && !imageData[selectedVariantId][0].thumbnail && productImage) {
      imageData[selectedVariantId][0].thumbnail = productImage
      imageData[selectedVariantId][0].source = productImage
    }

    // Add variant images (up to 4 total)
    for (const [index, item] of imageData[selectedVariantId].entries()) {
      if (totalThumbs === 4) break

      carouselMedia.push(item)
      this.thumbContainer.appendChild(this.createThumbnail(item, productTitle, index))

      totalThumbs += 1
    }

    // Display default image
    if (defaultImage) {
      this.displayImage.dataset.src = defaultImage.thumbnail
      this.selectImage(defaultImage.type, defaultImage.source)
    }

    this.prodImageCarouselHandler(carouselMedia, isSuggestedPairing)
  }

  /**
   * Create a thumbnail element
   * @param {MetaData} data - Media data
   * @param {string} productTitle - Title of the product
   * @param {number} index - Index of the thumbnail
   * @returns {HTMLElement} Thumbnail element
   */
  createThumbnail(data, productTitle, index) {
    const thumb = document.createElement("div")

    thumb.id = data.source
    thumb.className = "thumbnail aspect-[7/8] w-full object-cover object-center"

    thumb.innerHTML = `<img
      id="${data.type}-*-${data.source}"
      class="product-thumb aspect-[7/8] w-full object-cover object-center"
      src="${data.thumbnail}"
      alt="${productTitle} - thumbnail photo #${index + 1}"
      width="123"
      height="140"
    >`

    thumb.dataset.source = data.source
    thumb.dataset.type = data.type

    thumb.addEventListener("click", (event) => {
      if (event.currentTarget.dataset.type && event.currentTarget.dataset.source) {
        this.selectImage(event.currentTarget.dataset.type, event.currentTarget.dataset.source)
      }
    })

    return thumb
  }
}
